import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getToken } from '../services/api';
import { useLocale } from '../context/LocaleContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const createId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const getStoredId = (storage, key) => {
  let value = storage.getItem(key);
  if (!value) {
    value = createId();
    storage.setItem(key, value);
  }
  return value;
};

const send = (endpoint, body) => {
  const token = getToken();
  return fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => null);
};

const normalizeLocalizedPath = (pathname) => {
  const parts = String(pathname || '/').split('/').filter(Boolean);
  if (parts[0] && /^[a-z]{2,3}(?:-[a-z]{2})?$/i.test(parts[0])) parts.shift();
  return `/${parts.join('/')}` || '/';
};

const contentTitleForPath = (pathname) => {
  const path = normalizeLocalizedPath(pathname);
  let selector = '';
  if (path.startsWith('/products/')) selector = '.product-detail-title';
  else if (path.startsWith('/articles/')) selector = '.article-detail-hero-content h1';
  else if (path.startsWith('/projects/')) selector = '.project-detail-title';

  const contentTitle = selector ? document.querySelector(selector)?.textContent?.trim() : '';
  return contentTitle || document.title || '';
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const { locale } = useLocale();
  const sessionId = useRef(getStoredId(sessionStorage, 'haryad_analytics_session'));
  const visitorId = useRef(getStoredId(localStorage, 'haryad_analytics_visitor'));

  useEffect(() => {
    if (location.pathname === '/admin-panel' || location.pathname.startsWith('/admin-panel/')) return undefined;
    const pagePath = `${location.pathname}${location.search}`;
    const initialTitle = contentTitleForPath(location.pathname);
    const payload = {
      sessionId: sessionId.current,
      visitorId: visitorId.current,
      pagePath,
      pageTitle: initialTitle,
      referrer: document.referrer,
      language: locale.code,
    };
    send('/api/analytics/track', payload);

    let lastSyncedTitle = initialTitle;
    const syncCurrentPage = () => {
      if (document.visibilityState !== 'visible') return;
      const currentTitle = contentTitleForPath(location.pathname);
      send('/api/analytics/heartbeat', {
        sessionId: sessionId.current,
        pagePath,
        pageTitle: currentTitle,
      });
      lastSyncedTitle = currentTitle;
    };

    // Detail pages fetch their actual content after the route has mounted. Check briefly
    // after navigation so the analytics row receives the real product/article/project title.
    let fastChecks = 0;
    const titleSyncTimer = window.setInterval(() => {
      fastChecks += 1;
      const currentTitle = contentTitleForPath(location.pathname);
      if (currentTitle && currentTitle !== lastSyncedTitle) syncCurrentPage();
      if (fastChecks >= 40) window.clearInterval(titleSyncTimer);
    }, 250);

    const heartbeatTimer = window.setInterval(syncCurrentPage, 30000);
    document.addEventListener('visibilitychange', syncCurrentPage);
    return () => {
      window.clearInterval(titleSyncTimer);
      window.clearInterval(heartbeatTimer);
      document.removeEventListener('visibilitychange', syncCurrentPage);
    };
  }, [locale.code, location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
